import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Truck, Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_OPTIONS = [
    { value: 'demenagement_local', label: 'Déménagement local' },
    { value: 'demenagement_longue_distance', label: 'Déménagement longue distance' },
    { value: 'chargement', label: 'Chargement seul' },
    { value: 'dechargement', label: 'Déchargement seul' },
    { value: 'garde_meuble', label: 'Garde-meuble' },
    { value: 'autre', label: 'Autre' },
];

const ASC_OPTIONS = [
    { value: 'non', label: 'Aucun' },
    { value: 'petit', label: 'Petit (1-3 pers.)' },
    { value: '4_6', label: '4 à 6 personnes' },
    { value: 'grand', label: 'Grand' },
];

const EMPTY_FORM = {
    client_nom: '',
    client_telephone: '',
    client_email: '',
    date: '',
    heure: '08:00',
    type_prestation: 'demenagement_local',
    adresse_depart: '',
    etage_depart: '0',
    ascenseur_depart: 'non',
    aot_depart: 'non',
    monte_meuble_depart: 'non',
    portage_depart: '',
    adresse_arrivee: '',
    etage_arrivee: '0',
    ascenseur_arrivee: 'non',
    aot_arrivee: 'non',
    monte_meuble_arrivee: 'non',
    portage_arrivee: '',
    volume: 0,
    camion_20m3: 0,
    camion_30m3: 0,
    demenageurs: 0,
    sous_traitant_nom: '',
    prix_ht: 0,
    prix_ttc: 0,
    remarque: '',
};

/**
 * Modal form to create / edit a sous-traitance event.
 *
 * Props:
 *   open: boolean
 *   onOpenChange(open): toggle
 *   eventId: string | null (null => create mode, id => edit mode)
 *   onSaved(): callback after successful save (parent refreshes the calendar)
 */
export default function SousTraitanceForm({ open, onOpenChange, eventId = null, onSaved }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (!eventId) {
            setForm({ ...EMPTY_FORM, date: new Date().toISOString().slice(0, 10) });
            return;
        }
        // Edit mode: pre-fill from existing event
        setLoading(true);
        axios
            .get(`${API_URL}/api/sous-traitance/events/${eventId}`, { withCredentials: true })
            .then((res) => {
                setForm({ ...EMPTY_FORM, ...res.data });
            })
            .catch(() => toast.error('Impossible de charger la sous-traitance'))
            .finally(() => setLoading(false));
    }, [open, eventId]);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.client_nom.trim()) {
            toast.error('Le nom du client est requis');
            return;
        }
        if (!form.date) {
            toast.error('La date est requise');
            return;
        }
        setSaving(true);
        try {
            // Coerce numeric fields
            const payload = {
                ...form,
                volume: Number(form.volume) || 0,
                camion_20m3: Number(form.camion_20m3) || 0,
                camion_30m3: Number(form.camion_30m3) || 0,
                demenageurs: Number(form.demenageurs) || 0,
                prix_ht: Number(form.prix_ht) || 0,
                prix_ttc: Number(form.prix_ttc) || 0,
            };
            if (eventId) {
                await axios.put(`${API_URL}/api/sous-traitance/events/${eventId}`, payload, { withCredentials: true });
                toast.success('Sous-traitance mise à jour');
            } else {
                await axios.post(`${API_URL}/api/sous-traitance/events`, payload, { withCredentials: true });
                toast.success('Sous-traitance créée');
            }
            onOpenChange(false);
            if (onSaved) onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur de sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl max-h-[90vh] overflow-y-auto"
                data-testid="sous-traitance-form-modal"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-orange-700">
                        <Truck className="w-5 h-5" />
                        {eventId ? 'Modifier la sous-traitance' : 'Nouveau déménagement en sous-traitance'}
                    </DialogTitle>
                    <DialogDescription>
                        Informations pour la lettre de voiture. Champ <span className="text-red-500">*</span> requis.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="py-10 text-center text-gray-400">
                        <Loader2 className="w-5 h-5 animate-spin inline" />
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* CLIENT */}
                        <section>
                            <h3 className="text-xs font-bold text-gray-500 uppercase mb-2 border-b border-gray-200 pb-1">
                                Client
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <Label>Nom <span className="text-red-500">*</span></Label>
                                    <Input
                                        value={form.client_nom}
                                        onChange={(e) => update('client_nom', e.target.value)}
                                        data-testid="st-client-nom"
                                        required
                                    />
                                </div>
                                <div>
                                    <Label>Téléphone</Label>
                                    <Input
                                        value={form.client_telephone}
                                        onChange={(e) => update('client_telephone', e.target.value)}
                                        data-testid="st-client-tel"
                                    />
                                </div>
                                <div>
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={form.client_email}
                                        onChange={(e) => update('client_email', e.target.value)}
                                        data-testid="st-client-email"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* PLANNING */}
                        <section>
                            <h3 className="text-xs font-bold text-gray-500 uppercase mb-2 border-b border-gray-200 pb-1">
                                Planning
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <Label>Date <span className="text-red-500">*</span></Label>
                                    <Input
                                        type="date"
                                        value={form.date}
                                        onChange={(e) => update('date', e.target.value)}
                                        data-testid="st-date"
                                        required
                                    />
                                </div>
                                <div>
                                    <Label>Heure</Label>
                                    <Input
                                        type="time"
                                        value={form.heure}
                                        onChange={(e) => update('heure', e.target.value)}
                                        data-testid="st-heure"
                                    />
                                </div>
                                <div>
                                    <Label>Type de prestation</Label>
                                    <Select value={form.type_prestation} onValueChange={(v) => update('type_prestation', v)}>
                                        <SelectTrigger data-testid="st-type"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {TYPE_OPTIONS.map((o) => (
                                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </section>

                        {/* DEPART */}
                        <section>
                            <h3 className="text-xs font-bold text-gray-500 uppercase mb-2 border-b border-gray-200 pb-1">
                                Adresse de départ
                            </h3>
                            <div className="space-y-3">
                                <Input
                                    placeholder="N°, voie, ville, code postal"
                                    value={form.adresse_depart}
                                    onChange={(e) => update('adresse_depart', e.target.value)}
                                    data-testid="st-adr-dep"
                                />
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                    <div>
                                        <Label>Étage</Label>
                                        <Input
                                            value={form.etage_depart}
                                            onChange={(e) => update('etage_depart', e.target.value)}
                                            data-testid="st-etage-dep"
                                        />
                                    </div>
                                    <div>
                                        <Label>Ascenseur</Label>
                                        <Select value={form.ascenseur_depart} onValueChange={(v) => update('ascenseur_depart', v)}>
                                            <SelectTrigger data-testid="st-asc-dep"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {ASC_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>AOT</Label>
                                        <Select value={form.aot_depart} onValueChange={(v) => update('aot_depart', v)}>
                                            <SelectTrigger data-testid="st-aot-dep"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="non">Non</SelectItem>
                                                <SelectItem value="oui">Oui</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Monte-meuble</Label>
                                        <Select value={form.monte_meuble_depart} onValueChange={(v) => update('monte_meuble_depart', v)}>
                                            <SelectTrigger data-testid="st-mm-dep"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="non">Non</SelectItem>
                                                <SelectItem value="oui">Oui</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Portage (m)</Label>
                                        <Input
                                            value={form.portage_depart}
                                            onChange={(e) => update('portage_depart', e.target.value)}
                                            data-testid="st-port-dep"
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* ARRIVEE */}
                        <section>
                            <h3 className="text-xs font-bold text-gray-500 uppercase mb-2 border-b border-gray-200 pb-1">
                                Adresse d'arrivée
                            </h3>
                            <div className="space-y-3">
                                <Input
                                    placeholder="N°, voie, ville, code postal"
                                    value={form.adresse_arrivee}
                                    onChange={(e) => update('adresse_arrivee', e.target.value)}
                                    data-testid="st-adr-arr"
                                />
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                    <div>
                                        <Label>Étage</Label>
                                        <Input
                                            value={form.etage_arrivee}
                                            onChange={(e) => update('etage_arrivee', e.target.value)}
                                            data-testid="st-etage-arr"
                                        />
                                    </div>
                                    <div>
                                        <Label>Ascenseur</Label>
                                        <Select value={form.ascenseur_arrivee} onValueChange={(v) => update('ascenseur_arrivee', v)}>
                                            <SelectTrigger data-testid="st-asc-arr"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {ASC_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>AOT</Label>
                                        <Select value={form.aot_arrivee} onValueChange={(v) => update('aot_arrivee', v)}>
                                            <SelectTrigger data-testid="st-aot-arr"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="non">Non</SelectItem>
                                                <SelectItem value="oui">Oui</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Monte-meuble</Label>
                                        <Select value={form.monte_meuble_arrivee} onValueChange={(v) => update('monte_meuble_arrivee', v)}>
                                            <SelectTrigger data-testid="st-mm-arr"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="non">Non</SelectItem>
                                                <SelectItem value="oui">Oui</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Portage (m)</Label>
                                        <Input
                                            value={form.portage_arrivee}
                                            onChange={(e) => update('portage_arrivee', e.target.value)}
                                            data-testid="st-port-arr"
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* RESSOURCES */}
                        <section>
                            <h3 className="text-xs font-bold text-gray-500 uppercase mb-2 border-b border-gray-200 pb-1">
                                Ressources
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div>
                                    <Label>Volume (m³)</Label>
                                    <Input
                                        type="number"
                                        value={form.volume}
                                        onChange={(e) => update('volume', e.target.value)}
                                        data-testid="st-volume"
                                    />
                                </div>
                                <div>
                                    <Label>Camions 20m³</Label>
                                    <Input
                                        type="number"
                                        value={form.camion_20m3}
                                        onChange={(e) => update('camion_20m3', e.target.value)}
                                        data-testid="st-camion-20"
                                    />
                                </div>
                                <div>
                                    <Label>Camions 30m³</Label>
                                    <Input
                                        type="number"
                                        value={form.camion_30m3}
                                        onChange={(e) => update('camion_30m3', e.target.value)}
                                        data-testid="st-camion-30"
                                    />
                                </div>
                                <div>
                                    <Label>Déménageurs</Label>
                                    <Input
                                        type="number"
                                        value={form.demenageurs}
                                        onChange={(e) => update('demenageurs', e.target.value)}
                                        data-testid="st-demenageurs"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* SOUS-TRAITANCE */}
                        <section>
                            <h3 className="text-xs font-bold text-orange-700 uppercase mb-2 border-b border-orange-200 pb-1">
                                Sous-traitance
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <Label>Sous-traitant</Label>
                                    <Input
                                        placeholder="Nom de l'entreprise"
                                        value={form.sous_traitant_nom}
                                        onChange={(e) => update('sous_traitant_nom', e.target.value)}
                                        data-testid="st-sous-traitant"
                                    />
                                </div>
                                <div>
                                    <Label>Prix HT (€)</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={form.prix_ht}
                                        onChange={(e) => update('prix_ht', e.target.value)}
                                        data-testid="st-prix-ht"
                                    />
                                </div>
                                <div>
                                    <Label>Prix TTC (€)</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={form.prix_ttc}
                                        onChange={(e) => update('prix_ttc', e.target.value)}
                                        data-testid="st-prix-ttc"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* REMARQUE */}
                        <section>
                            <Label>Remarque</Label>
                            <Textarea
                                rows={3}
                                value={form.remarque}
                                onChange={(e) => update('remarque', e.target.value)}
                                placeholder="Informations utiles : piano, animaux, accès difficile…"
                                data-testid="st-remarque"
                            />
                        </section>

                        <div className="flex justify-end gap-2 pt-2 border-t">
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                                Annuler
                            </Button>
                            <Button
                                type="submit"
                                disabled={saving}
                                className="bg-orange-700 hover:bg-orange-800 text-white gap-2"
                                data-testid="st-submit-btn"
                            >
                                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                                {eventId ? 'Enregistrer' : 'Créer la sous-traitance'}
                            </Button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
